import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Pagination,
    Card,
    CardContent,
    Chip,
    Tooltip,
    Avatar,
    Stack,
    Fade,
    Skeleton,
    Drawer,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
    useMediaQuery,
    ThemeProvider,
    createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import CallEndIcon from "@mui/icons-material/CallEnd";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventIcon from "@mui/icons-material/Event";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PsychologyIcon from "@mui/icons-material/Psychology";
import InboxIcon from "@mui/icons-material/Inbox";
import { toast } from "react-toastify";
import { UserContext } from "../Contexts/LoginContext";
import { EnquiryContext } from "../Contexts/EnquiryContext";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { url } from "../Components/url";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";
import { formatDateIST, formatDateTimeIST, parseUtcDateLike, IST_TIME_ZONE } from "../utils/datetimeIst";
import styles from "./FollowUps.module.scss";

const baseThemeOptions = {
    palette: {
        secondary: { main: "#f5365c" },
        success: { main: "#2dce89" },
        warning: { main: "#fb6340" },
        info: { main: "#11cdef" },
    },
    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        h4: { fontWeight: 600, letterSpacing: "-0.02em" },
        button: { textTransform: "none", fontWeight: 500 },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
}));

// Priority scoring (spec)
const LEAD_SCORE = { Hot: 50, Warm: 30, Cold: 10, "Not Assigned": 5 };
const COURSE_SCORE = { has: 30, none: 5 };
const DAYS_SCORE = { "1-3": 40, "3-7": 30, "7+": 10, none: 0 };
const STATUS_SCORE = {
    "Application Started": 40,
    "Application Form Filled": 40,
    Contacted: 20,
    "New Enquiry": 10,
};

function parseDaysSince(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    const days = Math.floor((now - d) / (24 * 60 * 60 * 1000));
    return days;
}

function getDaysBucket(days) {
    if (days == null) return "none";
    if (days <= 3) return "1-3";
    if (days <= 7) return "3-7";
    return "7+";
}

function computePriorityScore(row) {
    const lead = (row.lead_temperature || "Not Assigned").replace(/\s+/g, " ");
    const leadScore = LEAD_SCORE[lead] ?? 5;
    const hasCourse = !!(row.selected_course && String(row.selected_course).trim());
    const courseScore = hasCourse ? COURSE_SCORE.has : COURSE_SCORE.none;
    const days = parseDaysSince(row.last_contacted_at);
    const bucket = getDaysBucket(days);
    const daysScore = DAYS_SCORE[bucket] ?? 0;
    const status = row.status || "New Enquiry";
    const statusScore = STATUS_SCORE[status] ?? 10;
    return leadScore + courseScore + daysScore + statusScore;
}

function formatRelative(dateStr) {
    if (!dateStr) return "—";
    const d = parseUtcDateLike(dateStr);
    if (!d) return "—";
    const now = new Date();
    const days = Math.floor((now - d) / (24 * 60 * 60 * 1000));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
}

function istDayNumber(d) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: IST_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(d);
    const pick = (type) => parts.find((p) => p.type === type)?.value || "01";
    return Date.UTC(+pick("year"), +pick("month") - 1, +pick("day"));
}

function formatNextFollowUp(dateStr) {
    if (!dateStr) return "—";
    const d = parseUtcDateLike(dateStr);
    if (!d) return "—";
    const diff = (istDayNumber(d) - istDayNumber(new Date())) / (24 * 60 * 60 * 1000);
    if (diff < 0) return "Overdue";
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff <= 7) return `In ${diff} days`;
    return formatDateIST(d);
}

function isOverdue(dateStr) {
    if (!dateStr) return false;
    const d = parseUtcDateLike(dateStr);
    if (!d) return false;
    return istDayNumber(d) < istDayNumber(new Date());
}

const OUTCOMES = [
    "No Answer",
    "Interested",
    "Not Interested",
    "Requested Callback",
    "Application Started",
    "Appointment Booked",
];

const PAGE_SIZE_OPTIONS = [10, 12, 25, 50, 100];

const STATUS_OPTIONS = [
    "New Enquiry",
    "Contacted",
    "Application Started",
    "COE Requested",
    "Visa Submitted",
    "Visa Decision",
    "Registered",
    "Lost",
];

export default function FollowUps() {
    const { themeMode } = useTheme();
    const theme = useMemo(
        () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
        [themeMode]
    );
    const { username } = useContext(UserContext);
    const { setEnquiry } = useContext(EnquiryContext);
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [metrics, setMetrics] = useState({ calls_today: 0, followups_completed: 0, appointments_booked: 0, conversions: 0 });
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(!isMobile);
    const [filters, setFilters] = useState({
        assigned_to: "",
        lead_temperature: "",
        course: "",
        status: "",
        overdue: false,
    });
    const debouncedFilters = useDebouncedValue(filters, 400);
    const [statusOptions, setStatusOptions] = useState([]);
    const [counsellors, setCounsellors] = useState([]);
    const [drawerEnquiry, setDrawerEnquiry] = useState(null);
    const [drawerLogs, setDrawerLogs] = useState([]);
    const [outcome, setOutcome] = useState("");
    const [leadTemp, setLeadTemp] = useState("");
    const [statusUpdate, setStatusUpdate] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [script, setScript] = useState("");
    const [scriptLoading, setScriptLoading] = useState(false);
    const [bookModalOpen, setBookModalOpen] = useState(false);
    const [bookDate, setBookDate] = useState("");
    const [bookTime, setBookTime] = useState("");
    const [bookType, setBookType] = useState("Online");
    const [bookNotes, setBookNotes] = useState("");
    const [callingSession, setCallingSession] = useState(false);
    const [sessionIndex, setSessionIndex] = useState(0);

    const fetchMetrics = useCallback(async () => {
        try {
            const r = await fetchWithAuth(`${url}/followups/metrics`, { method: "GET", credentials: "include" });
            const j = await r.json();
            if (r.ok) setMetrics(j);
        } catch (_) {}
    }, []);

    const fetchStatuses = useCallback(async () => {
        try {
            const r = await fetchWithAuth(`${url}/status/all`, { method: "GET", credentials: "include" });
            const j = await r.json();
            if (r.ok) setStatusOptions(Array.isArray(j) ? j : []);
        } catch (_) {}
    }, []);

    const fetchCounsellors = useCallback(async () => {
        try {
            const r = await fetchWithAuth(`${url}/followups/counsellors`, { method: "GET", credentials: "include" });
            const j = await r.json();
            if (r.ok) setCounsellors(Array.isArray(j) ? j : []);
        } catch (_) {}
    }, []);

    const fetchEnquiries = useCallback(async (pageNum = currentPage, pageSizeOverride = pageSize, filtersOverride = null) => {
        const f = filtersOverride !== null ? filtersOverride : debouncedFilters;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("page", String(pageNum));
            params.set("page_size", String(pageSizeOverride));
            if (f.assigned_to) params.set("assigned_to", f.assigned_to);
            if (f.lead_temperature) params.set("lead_temperature", f.lead_temperature);
            if (f.course) params.set("course", f.course);
            if (f.status) params.set("status", f.status);
            if (f.overdue) params.set("overdue", "true");
            const r = await fetchWithAuth(`${url}/followups/enquiries?${params}`, { method: "GET", credentials: "include" });
            const j = await r.json();
            if (r.ok) {
                const list = j.data || [];
                const withScore = list.map((row) => ({ ...row, priority_score: computePriorityScore(row) }));
                withScore.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
                setData(withScore);
                setTotalCount(j.total ?? 0);
            } else toast.error(j.error || "Failed to load follow-ups");
        } catch (e) {
            toast.error("Failed to load follow-ups");
        } finally {
            setLoading(false);
        }
    }, [debouncedFilters, currentPage, pageSize]);

    useEffect(() => {
        fetchStatuses();
        fetchCounsellors();
    }, [fetchStatuses, fetchCounsellors]);

    useEffect(() => {
        fetchEnquiries();
    }, [fetchEnquiries]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters.assigned_to, filters.lead_temperature, filters.course, filters.status, filters.overdue]);

    useEffect(() => {
        fetchMetrics();
    }, [fetchMetrics]);

    const fetchLogs = useCallback(async (enquiryId) => {
        try {
            const r = await fetchWithAuth(`${url}/followups/logs/${enquiryId}`, { method: "GET", credentials: "include" });
            const j = await r.json();
            if (r.ok) setDrawerLogs(j.logs || []);
        } catch (_) {}
    }, []);

    const openDrawer = useCallback((enquiry) => {
        setDrawerEnquiry(enquiry);
        setOutcome("");
        setLeadTemp(enquiry?.lead_temperature || "");
        setStatusUpdate(enquiry?.status || "");
        setNotes("");
        setScript("");
        if (enquiry?.id) fetchLogs(enquiry.id);
    }, [fetchLogs]);

    const closeDrawer = useCallback(() => {
        setDrawerEnquiry(null);
        setDrawerLogs([]);
    }, []);

    const handleSaveLog = async () => {
        if (!drawerEnquiry?.id) return;
        setSaving(true);
        try {
            const r = await fetchWithAuth(`${url}/followups/log`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    enquiry_id: drawerEnquiry.id,
                    outcome: outcome || undefined,
                    notes: notes || undefined,
                    lead_temperature: leadTemp || undefined,
                    status: statusUpdate || undefined,
                }),
            });
            const j = await r.json();
            if (r.ok) {
                toast.success("Follow-up saved");
                fetchLogs(drawerEnquiry.id);
                fetchEnquiries();
                fetchMetrics();
                setOutcome("");
                setNotes("");
            } else toast.error(j.error || "Failed to save");
        } catch (e) {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const generateScript = useCallback(async () => {
        if (!drawerEnquiry) return;
        setScriptLoading(true);
        setScript("");
        try {
            const r = await fetchWithAuth(`${url}/followups/ai-script`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    course: drawerEnquiry.selected_course || "study abroad",
                    lead_temperature: drawerEnquiry.lead_temperature || "Warm",
                    name: `${drawerEnquiry.first_name || ""} ${drawerEnquiry.last_name || ""}`.trim() || "the student",
                }),
            });
            const j = await r.json();
            if (r.ok && j.script) setScript(j.script);
            else if (j.error) toast.error(j.error);
        } catch (e) {
            toast.error("Failed to generate script");
        } finally {
            setScriptLoading(false);
        }
    }, [drawerEnquiry]);

    const handleBookAppointment = () => {
        setBookModalOpen(true);
        setBookDate("");
        setBookTime("");
        setBookType("Online");
        setBookNotes("");
    };

    const resetFilters = () => {
        setFilters({ assigned_to: "", lead_temperature: "", course: "", status: "", overdue: false });
        toast.info("Filters cleared");
    };

    const handleViewEnquiry = (enquiry) => {
        const dataDictionary = enquiry && !Array.isArray(enquiry) ? enquiry : { 0: enquiry };
        setEnquiry(dataDictionary);
        navigate("/enquiry_profile", { state: { enquiry } });
    };

    const handleCall = (enquiry) => {
        const phone = enquiry?.phone_number;
        if (phone) window.open(`tel:${phone.replace(/\s/g, "")}`, "_self");
        else toast.warning("No phone number");
    };

    const sessionList = useMemo(() => (callingSession ? data : []), [callingSession, data]);
    const currentSessionEnquiry = sessionList[sessionIndex] || null;

    useEffect(() => {
        if (callingSession && currentSessionEnquiry) openDrawer(currentSessionEnquiry);
    }, [callingSession, sessionIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleKeyDown = useCallback(
        (e) => {
            if (!drawerEnquiry || e.repeat) return;
            if (e.key === "1") {
                setOutcome("No Answer");
                e.preventDefault();
            } else if (e.key === "2") {
                setOutcome("Interested");
                e.preventDefault();
            } else if (e.key === "3") {
                setOutcome("Requested Callback");
                e.preventDefault();
            }
        },
        [drawerEnquiry]
    );

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const phoneForLink = (enquiry) => {
        const p = enquiry?.phone_number;
        if (!p) return "";
        const digits = p.replace(/\D/g, "");
        return digits.length <= 10 ? `+94${digits}` : p.startsWith("+") ? p : `+${p}`;
    };

    return (
        <ThemeProvider theme={theme}>
            <Box className={styles.root}>
                <Paper elevation={0} className={styles.contentPaper}>
                            <Box sx={{ marginBottom: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box>
                                        <Typography variant="h4" className={styles.pageTitle}>
                                            Follow-Up Call Center
                                        </Typography>
                                        <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                                            Recommended enquiries to follow up with.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box className={styles.metricsGrid}>
                                <Card variant="outlined" className={styles.metricCard}>
                                    <CardContent>
                                        <Typography className={styles.metricValue}>{metrics.calls_today ?? 0}</Typography>
                                        <Typography className={styles.metricLabel}>Calls Today</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" className={styles.metricCard}>
                                    <CardContent>
                                        <Typography className={styles.metricValue}>{metrics.followups_completed ?? 0}</Typography>
                                        <Typography className={styles.metricLabel}>Follow-Ups Completed</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" className={styles.metricCard}>
                                    <CardContent>
                                        <Typography className={styles.metricValue}>{metrics.appointments_booked ?? 0}</Typography>
                                        <Typography className={styles.metricLabel}>Appointments Booked</Typography>
                                    </CardContent>
                                </Card>
                                <Card variant="outlined" className={styles.metricCard}>
                                    <CardContent>
                                        <Typography className={styles.metricValue}>{metrics.conversions ?? 0}</Typography>
                                        <Typography className={styles.metricLabel}>Conversions</Typography>
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box className={styles.header}>
                                <Button
                                    startIcon={<FilterListIcon />}
                                    onClick={() => setFilterOpen(!filterOpen)}
                                    variant="outlined"
                                    size="small"
                                >
                                    {filterOpen ? "Hide Filters" : "Show Filters"}
                                </Button>
                                <Box className={styles.headerActions}>
                                    <Button
                                        variant="outlined"
                                        color={callingSession ? "primary" : "inherit"}
                                        startIcon={<PhoneIcon />}
                                        onClick={() => {
                                            setCallingSession(!callingSession);
                                            if (!callingSession) setSessionIndex(0);
                                        }}
                                    >
                                        {callingSession ? "End Session" : "Start Calling Session"}
                                    </Button>
                                </Box>
                            </Box>

                            {filterOpen && (
                                <Fade in={filterOpen}>
                                    <FilterPaper variant="outlined">
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Assigned Counsellor</InputLabel>
                                                    <Select
                                                        value={filters.assigned_to}
                                                        label="Assigned Counsellor"
                                                        onChange={(e) => setFilters((f) => ({ ...f, assigned_to: e.target.value }))}
                                                    >
                                                        <MenuItem value="">All</MenuItem>
                                                        {counsellors.map((c) => (
                                                            <MenuItem key={c.id} value={String(c.id)}>
                                                                {c.first_name} {c.last_name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Lead Temperature</InputLabel>
                                                    <Select
                                                        value={filters.lead_temperature}
                                                        label="Lead Temperature"
                                                        onChange={(e) => setFilters((f) => ({ ...f, lead_temperature: e.target.value }))}
                                                    >
                                                        <MenuItem value="">All</MenuItem>
                                                        <MenuItem value="Hot">Hot</MenuItem>
                                                        <MenuItem value="Warm">Warm</MenuItem>
                                                        <MenuItem value="Cold">Cold</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Course"
                                                    value={filters.course}
                                                    onChange={(e) => setFilters((f) => ({ ...f, course: e.target.value }))}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        value={filters.status}
                                                        label="Status"
                                                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                                                    >
                                                        <MenuItem value="">All</MenuItem>
                                                        {statusOptions.map((s, i) => (
                                                            <MenuItem key={i} value={s}>
                                                                {s}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Overdue</InputLabel>
                                                    <Select
                                                        value={filters.overdue ? "yes" : "no"}
                                                        label="Overdue"
                                                        onChange={(e) => setFilters((f) => ({ ...f, overdue: e.target.value === "yes" }))}
                                                    >
                                                        <MenuItem value="no">No</MenuItem>
                                                        <MenuItem value="yes">Overdue only</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} className={styles.filterActions}>
                                                <Button variant="contained" startIcon={<SearchIcon />} onClick={() => { setCurrentPage(1); fetchEnquiries(1, pageSize, filters); }}>
                                                    Search
                                                </Button>
                                                <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                                                    Reset
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </FilterPaper>
                                </Fade>
                            )}

                            {loading && (
                                <Box className={styles.loadingContainer}>
                                    <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                                    <Skeleton variant="rectangular" height={400} />
                                </Box>
                            )}

                            {!loading && data.length === 0 && (
                                <Box className={styles.emptyState}>
                                    <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary">
                                        No follow-ups found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Adjust filters or wait for new enquiries.
                                    </Typography>
                                </Box>
                            )}

                            {!loading && data.length > 0 && (
                                <>
                                    <Paper variant="outlined" className={styles.tableContainer}>
                                        <Table stickyHeader size="small" className={styles.followupsTable}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell className={styles.colName}>Name</TableCell>
                                                    <TableCell className={styles.colPhone}>Phone</TableCell>
                                                    <TableCell className={styles.colCourse}>Course</TableCell>
                                                    <TableCell className={styles.colStatus}>Status</TableCell>
                                                    <TableCell className={styles.colLeadTemp}>Lead</TableCell>
                                                    <TableCell className={styles.colPriority}>Priority</TableCell>
                                                    <TableCell className={styles.colLastContact}>Last Contact</TableCell>
                                                    <TableCell className={styles.colNextFollowUp}>Next Follow-Up</TableCell>
                                                    <TableCell className={styles.colActions}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data.map((row) => (
                                                    <TableRow
                                                        key={row.id}
                                                        hover
                                                        onClick={() => openDrawer(row)}
                                                        className={`${styles.tableRow} ${isOverdue(row.next_followup_date) ? styles.rowOverdue : ""}`}
                                                    >
                                                        <TableCell className={styles.colName}>
                                                            <Box className={styles.nameCell}>
                                                                <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.main", fontSize: 12 }}>
                                                                    {(row.first_name || "N").charAt(0)}
                                                                </Avatar>
                                                                <Box className={styles.nameCellText}>
                                                                    <Typography variant="body2" className={styles.primaryText} noWrap>
                                                                        {row.first_name} {row.last_name}
                                                                    </Typography>
                                                                    <Typography variant="caption" className={styles.secondaryText} noWrap>
                                                                        {row.email}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell className={styles.colPhone}>
                                                            <a
                                                                href={`tel:${phoneForLink(row)}`}
                                                                className={styles.phoneLink}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {row.phone_number || "—"}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell className={styles.colCourse}>{row.selected_course || "—"}</TableCell>
                                                        <TableCell className={styles.colStatus}>
                                                            <Chip label={row.status || "—"} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell className={styles.colLeadTemp}>
                                                            <Chip
                                                                label={row.lead_temperature || "—"}
                                                                size="small"
                                                                color={row.lead_temperature === "Hot" ? "error" : row.lead_temperature === "Warm" ? "warning" : "default"}
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell className={styles.colPriority}>{row.priority_score ?? 0}</TableCell>
                                                        <TableCell className={styles.colLastContact}>{formatRelative(row.last_contacted_at)}</TableCell>
                                                        <TableCell className={styles.colNextFollowUp}>
                                                            {isOverdue(row.next_followup_date) ? (
                                                                <span className={styles.overdueBadge}>Overdue</span>
                                                            ) : (
                                                                <span className={styles.countdown}>{formatNextFollowUp(row.next_followup_date)}</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className={styles.colActions} onClick={(e) => e.stopPropagation()}>
                                                            <Stack direction="row" spacing={1} className={styles.actionButtons}>
                                                                <Tooltip title="Call">
                                                                    <IconButton size="small" color="primary" onClick={() => handleCall(row)}>
                                                                        <PhoneIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Open Enquiry">
                                                                    <IconButton size="small" onClick={() => handleViewEnquiry(row)}>
                                                                        <OpenInNewIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                    <Box className={styles.pagination}>
                                        <Typography variant="body2" color="text.secondary">
                                            Showing {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                <InputLabel>Per page</InputLabel>
                                                <Select
                                                    value={pageSize}
                                                    label="Per page"
                                                    onChange={(e) => {
                                                        setPageSize(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Pagination
                                                count={Math.max(1, Math.ceil(totalCount / pageSize))}
                                                page={currentPage}
                                                onChange={(_, page) => setCurrentPage(page)}
                                                color="primary"
                                                shape="rounded"
                                                showFirstButton
                                                showLastButton
                                            />
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </Paper>

                <Drawer
                    anchor="right"
                    open={!!drawerEnquiry}
                    onClose={closeDrawer}
                    PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
                >
                    <Box className={styles.drawerContent}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6">Follow-Up</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {callingSession && sessionList.length > 0 && (
                                    <>
                                        <IconButton
                                            size="small"
                                            disabled={sessionIndex <= 0}
                                            onClick={() => setSessionIndex((i) => Math.max(0, i - 1))}
                                        >
                                            <KeyboardArrowLeftIcon />
                                        </IconButton>
                                        <Typography variant="caption" color="text.secondary">
                                            {sessionIndex + 1} / {sessionList.length}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            disabled={sessionIndex >= sessionList.length - 1}
                                            onClick={() => setSessionIndex((i) => Math.min(sessionList.length - 1, i + 1))}
                                        >
                                            <KeyboardArrowRightIcon />
                                        </IconButton>
                                    </>
                                )}
                                <IconButton size="small" onClick={closeDrawer}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Box>

                        {drawerEnquiry && (
                            <>
                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Enquiry Summary</Typography>
                                    <Typography variant="body2"><strong>Name:</strong> {drawerEnquiry.first_name} {drawerEnquiry.last_name}</Typography>
                                    <Typography variant="body2"><strong>Phone:</strong> {drawerEnquiry.phone_number || "—"}</Typography>
                                    <Typography variant="body2"><strong>Email:</strong> {drawerEnquiry.email || "—"}</Typography>
                                    <Typography variant="body2"><strong>Course:</strong> {drawerEnquiry.selected_course || "—"}</Typography>
                                    <Typography variant="body2"><strong>Country:</strong> {drawerEnquiry.address || "—"}</Typography>
                                    <Typography variant="body2"><strong>Lead:</strong> {drawerEnquiry.lead_temperature || "—"}</Typography>
                                    <Typography variant="body2"><strong>Status:</strong> {drawerEnquiry.status || "—"}</Typography>
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Call Actions</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<PhoneIcon />}
                                            onClick={() => handleCall(drawerEnquiry)}
                                        >
                                            Call
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<EmailIcon />}
                                            href={`mailto:${drawerEnquiry.email}`}
                                        >
                                            Email
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            href={`https://wa.me/${phoneForLink(drawerEnquiry).replace("+", "")}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            WhatsApp
                                        </Button>
                                    </Stack>
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Follow-Up Outcome</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={outcome}
                                            displayEmpty
                                            onChange={(e) => setOutcome(e.target.value)}
                                            renderValue={(v) => v || "Select outcome"}
                                        >
                                            {OUTCOMES.map((o) => (
                                                <MenuItem key={o} value={o}>{o}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Shortcuts: 1 = No Answer, 2 = Interested, 3 = Callback
                                    </Typography>
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Lead Temperature</Typography>
                                    <ToggleButtonGroup
                                        value={leadTemp}
                                        exclusive
                                        onChange={(_, v) => v != null && setLeadTemp(v)}
                                        size="small"
                                    >
                                        <ToggleButton value="Hot">Hot</ToggleButton>
                                        <ToggleButton value="Warm">Warm</ToggleButton>
                                        <ToggleButton value="Cold">Cold</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Status Update</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={statusUpdate}
                                            displayEmpty
                                            onChange={(e) => setStatusUpdate(e.target.value)}
                                            renderValue={(v) => v || "Select status"}
                                        >
                                            {STATUS_OPTIONS.map((s) => (
                                                <MenuItem key={s} value={s}>{s}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Comment / Call Notes</Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        placeholder="Record call notes..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Button variant="outlined" fullWidth startIcon={<EventIcon />} onClick={handleBookAppointment}>
                                        Book Appointment
                                    </Button>
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>AI Call Script</Typography>
                                    <Button
                                        size="small"
                                        startIcon={scriptLoading ? <CircularProgress size={14} /> : <PsychologyIcon />}
                                        onClick={generateScript}
                                        disabled={scriptLoading}
                                    >
                                        {script ? "Regenerate Script" : "Generate Script"}
                                    </Button>
                                    {script && (
                                        <Box className={styles.scriptCard} sx={{ mt: 1 }}>
                                            {script}
                                        </Box>
                                    )}
                                </Box>

                                <Box className={styles.drawerSection}>
                                    <Typography className={styles.drawerTitle}>Recent logs</Typography>
                                    {drawerLogs.length === 0 && <Typography variant="body2" color="text.secondary">No logs yet.</Typography>}
                                    {drawerLogs.slice(0, 5).map((log) => (
                                        <Typography key={log.id} variant="body2" sx={{ mb: 0.5 }}>
                                            {log.created_at && formatDateTimeIST(log.created_at)} — {log.outcome || "—"} {log.notes ? `: ${log.notes}` : ""}
                                        </Typography>
                                    ))}
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Button variant="contained" fullWidth onClick={handleSaveLog} disabled={saving}>
                                        {saving ? "Saving…" : "Save Follow-Up"}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Drawer>

                <Dialog open={bookModalOpen} onClose={() => setBookModalOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Book Appointment</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField label="Date" type="date" size="small" fullWidth value={bookDate} onChange={(e) => setBookDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <TextField label="Time" type="time" size="small" fullWidth value={bookTime} onChange={(e) => setBookTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <FormControl fullWidth size="small">
                                <InputLabel>Meeting Type</InputLabel>
                                <Select value={bookType} label="Meeting Type" onChange={(e) => setBookType(e.target.value)}>
                                    <MenuItem value="Online">Online</MenuItem>
                                    <MenuItem value="In Person">In Person</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField label="Notes" multiline rows={2} size="small" fullWidth value={bookNotes} onChange={(e) => setBookNotes(e.target.value)} />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBookModalOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={() => { toast.info("Book appointment via Appointments calendar."); setBookModalOpen(false); }}>
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}
